import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Search, ShoppingCart, X, Trash2, Info } from "lucide-react";

/**
 * Phase 7A + Phase 7A.1 — Admin Purchases workspace.
 *
 * Add Purchase UX is human-readable:
 *   • Product / Item search over Shopify mirror AND internal purchase_items
 *   • + Create New Item inline (creates a reusable purchase_item)
 *   • + One-Time / Unclassified Item for historical unknowns
 *   • Split payment support (payments[])
 *   • Vendor autocomplete from previously-used names
 *
 * Backend:
 *   /api/admin/purchases                    (list/create/patch/cancel/totals)
 *   /api/admin/purchases/vendors/suggest    (autocomplete)
 *   /api/admin/purchase-items               (search/create)
 *   /api/admin/shopify-products/search      (Shopify mirror)
 *   /api/admin/purchase-items/{id}/inventory-inference (box-opening inference)
 */
const PAY_METHODS = ["cash", "card", "zelle", "cash_app", "wise", "other"];
const PAY_LABELS = { cash: "Cash", card: "Card", zelle: "Zelle",
                     cash_app: "Cash App", wise: "WISE", other: "Other" };
const STATUS_TABS = ["all", "completed", "cancelled"];

function money(n) { return `$${(Number(n) || 0).toFixed(2)}`; }
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function AdminPurchases() {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (tab !== "all") params.status = tab;
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [l, t] = await Promise.all([
        api.get("/admin/purchases", { params }),
        api.get("/admin/purchases/totals",
                  { params: { date_from: dateFrom || undefined,
                              date_to: dateTo || undefined } }),
      ]);
      setRows(l.data.purchases || []);
      setTotals(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  async function cancelRow(id) {
    if (!window.confirm("Cancel this purchase?")) return;
    try {
      await api.post(`/admin/purchases/${id}/cancel`);
      toast.success("Purchase cancelled.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    }
  }

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)]" data-testid="admin-purchases">
      <div className="max-w-5xl mx-auto p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => (window.location.href = "/admin")}
                    className="text-[10px] uppercase text-[var(--smoke)] flex items-center gap-1">
              <ArrowLeft size={12}/> Admin
            </button>
            <div className="text-2xl font-black flex items-center gap-2">
              <ShoppingCart size={22}/> Purchases
            </div>
          </div>
          <button onClick={() => setShowAdd(true)}
                  data-testid="purchase-add-btn"
                  className="bg-[var(--panini-red,_#dc2626)] text-white text-sm font-bold rounded-2xl px-3 py-2 flex items-center gap-1">
            <Plus size={14}/> Add Purchase
          </button>
        </div>

        {totals && (
          <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white"
                    data-testid="purchase-totals">
            <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Totals (completed)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <Stat label="Total" value={money(totals.totals?.total)} testid="total-total"/>
              <Stat label="Count" value={totals.totals?.count || 0} testid="total-count"/>
              <Stat label="Subtotal" value={money(totals.totals?.subtotal)}/>
              <Stat label="Shipping" value={money(totals.totals?.shipping)}/>
            </div>
            {totals.by_vendor?.length > 0 && (
              <div className="pt-2 text-xs">
                <div className="text-[10px] uppercase text-[var(--smoke)] mb-0.5">By vendor</div>
                {totals.by_vendor.slice(0, 5).map((v) => (
                  <div key={v.vendor_name} className="flex justify-between">
                    <span>{v.vendor_name}</span>
                    <span className="font-mono">{money(v.total)} · {v.count}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <div className="flex gap-1 overflow-x-auto" data-testid="purchase-status-tabs">
          {STATUS_TABS.map((s) => (
            <button key={s}
                    data-testid={`purchase-tab-${s}`}
                    onClick={() => setTab(s)}
                    className={`text-xs px-3 py-1 rounded-full border-2 ${
                      s === tab
                        ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                        : "border-[var(--ink)]"
                    }`}>
              {s}
            </button>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Vendor · PO # · notes"
                 data-testid="purchase-search-input"
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className="flex-1 min-w-[180px] border rounded-xl px-3 py-2 text-sm"/>
          <input type="date" value={dateFrom}
                 onChange={(e) => setDateFrom(e.target.value)}
                 data-testid="purchase-date-from"
                 className="border rounded-xl px-2 py-2 text-sm"/>
          <input type="date" value={dateTo}
                 onChange={(e) => setDateTo(e.target.value)}
                 data-testid="purchase-date-to"
                 className="border rounded-xl px-2 py-2 text-sm"/>
          <button onClick={load} data-testid="purchase-search-btn"
                  className="bg-[var(--ink)] text-white rounded-xl px-3 text-sm font-semibold">
            <Search size={14}/>
          </button>
        </div>

        <div className="space-y-2" data-testid="purchase-rows">
          {loading && <div className="text-sm text-[var(--smoke)]">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-[var(--smoke)]" data-testid="purchases-empty">
              No purchases.
            </div>
          )}
          {rows.map((p) => (
            <div key={p.id} data-testid={`purchase-row-${p.id}`}
                  className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white text-sm space-y-1">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-bold truncate">{p.vendor_name}</div>
                  <div className="text-[11px] text-[var(--smoke)] font-mono">
                    {p.public_purchase_id} · {p.purchase_date}
                    {p.payments && p.payments.length > 1
                      ? ` · split (${p.payments.map(x => PAY_LABELS[x.method] || x.method).join(" + ")})`
                      : ` · ${PAY_LABELS[p.payment_method] || p.payment_method}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-black">{money(p.total_cost)}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    p.status === "completed" ? "bg-green-200" : "bg-gray-300"
                  }`}>{p.status}</span>
                </div>
              </div>
              <div className="text-xs text-[var(--smoke)] truncate">
                {(p.lines || []).map((l) => l.product_title || l.description
                  || l.variant || l.line_type).join(" · ")}
              </div>
              {p.notes && <div className="text-xs">{p.notes}</div>}
              {p.status === "completed" && (
                <div className="flex gap-1 pt-1">
                  <button onClick={() => setEditing(p)}
                          data-testid={`purchase-edit-${p.id}`}
                          className="text-[11px] border-2 border-[var(--ink)] rounded-full px-2 py-0.5">
                    Edit
                  </button>
                  <button onClick={() => cancelRow(p.id)}
                          data-testid={`purchase-cancel-${p.id}`}
                          className="text-[11px] border-2 border-red-500 text-red-700 rounded-full px-2 py-0.5">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {showAdd && <PurchaseEditor onClose={() => setShowAdd(false)}
                                    onSaved={() => { setShowAdd(false); load(); }}/>}
      {editing && <PurchaseEditor existing={editing}
                                    onClose={() => setEditing(null)}
                                    onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border rounded-xl px-2 py-1" data-testid={testid}>
      <div className="text-[10px] uppercase text-[var(--smoke)]">{label}</div>
      <div className="font-bold text-sm">{value}</div>
    </div>
  );
}

// ─── PICKER: unified Product / Item search ─────────────────────────
function ItemPicker({ onPick, onUnclassified }) {
  const [q, setQ] = useState("");
  const [shopify, setShopify] = useState([]);
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const h = setTimeout(async () => {
      try {
        const [sp, pi] = await Promise.all([
          // Phase 7A.1 #2 — variant flattening can turn one product
          // into many rows.  Ask for enough so multi-variant products
          // (e.g. Colombia · White / Blue / Gold Crumple Full Team
          // Set) don't silently hide the variant the owner is after.
          api.get("/admin/shopify-products/search",
                    { params: { q, limit: 40 } }).catch(() => ({ data: { results: [] } })),
          api.get("/admin/purchase-items",
                    { params: { q: q || undefined, limit: 12, active: true } }),
        ]);
        setShopify(sp.data?.results || []);
        setItems(pi.data?.purchase_items || []);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  return (
    <div className="border rounded-xl p-2 space-y-2" data-testid="item-picker">
      <div className="text-[10px] uppercase text-[var(--smoke)]">Search Product / Item</div>
      <input value={q} onChange={(e) => setQ(e.target.value)}
             placeholder="e.g. Gold Box, Regular Pack, WC 2022 White Set…"
             data-testid="item-picker-search"
             autoFocus
             className="w-full border rounded-lg px-2 py-1.5 text-sm"/>
      <div className="space-y-1 max-h-56 overflow-auto">
        {items.map((it) => (
          <button key={it.id} onClick={() => onPick({ kind: "purchase_item", item: it })}
                  data-testid={`pick-item-${it.id}`}
                  className="w-full text-left text-xs border rounded-lg px-2 py-1 hover:bg-gray-50">
            <b>{it.name}</b>
            {it.category && <span className="text-[var(--smoke)]"> · {it.category}</span>}
            {it.last_unit_cost != null &&
              <span className="text-[var(--smoke)]"> · last {money(it.last_unit_cost)}</span>}
            {it.shopify_variant_id && <span className="text-blue-600"> · Shopify</span>}
          </button>
        ))}
        {shopify.map((s) => (
          <button key={s.id} onClick={() => onPick({ kind: "shopify", shopify: s })}
                  data-testid={`pick-shopify-${s.shopify_variant_id || s.shopify_product_id}`}
                  className="w-full text-left text-xs border rounded-lg px-2 py-1 hover:bg-gray-50">
            <b>{s.title}</b>
            <span className="text-blue-600"> · Shopify</span>
            {s.unit_price > 0 && <span className="text-[var(--smoke)]"> · list {money(s.unit_price)}</span>}
          </button>
        ))}
        {items.length === 0 && shopify.length === 0 && (
          <div className="text-xs text-[var(--smoke)] italic">No matches.</div>
        )}
      </div>
      <div className="flex gap-1">
        <button onClick={() => setShowCreate(true)}
                data-testid="item-picker-create-btn"
                className="flex-1 text-xs border-2 border-[var(--ink)] rounded-full px-2 py-1">
          + Create New Item
        </button>
        <button onClick={onUnclassified}
                data-testid="item-picker-unclassified"
                className="flex-1 text-xs border-2 border-dashed border-[var(--ink)] rounded-full px-2 py-1">
          + One-Time / Unclassified
        </button>
      </div>
      {showCreate && <CreatePurchaseItemModal
                        prefillName={q}
                        onClose={() => setShowCreate(false)}
                        onCreated={(item) => { setShowCreate(false);
                                                 onPick({ kind: "purchase_item", item }); }}/>}
    </div>
  );
}

function CreatePurchaseItemModal({ prefillName, onClose, onCreated }) {
  const [f, setF] = useState({ name: prefillName || "", category: "",
                                 default_unit_cost: "",
                                 components: [] });
  const [busy, setBusy] = useState(false);
  const [componentQ, setComponentQ] = useState("");
  const [componentResults, setComponentResults] = useState([]);

  useEffect(() => {
    if (!componentQ) { setComponentResults([]); return; }
    const h = setTimeout(async () => {
      try {
        const r = await api.get("/admin/purchase-items",
                                    { params: { q: componentQ, limit: 12,
                                                active: true } });
        setComponentResults(r.data?.purchase_items || []);
      } catch { /* ignore */ }
    }, 180);
    return () => clearTimeout(h);
  }, [componentQ]);

  function addComponent(it) {
    if (f.components.some((c) => c.component_item_id === it.id)) return;
    setF({ ...f, components: [...f.components,
                                { component_item_id: it.id,
                                  component_name: it.name,
                                  quantity_per_parent: 1 }] });
    setComponentQ(""); setComponentResults([]);
  }
  function updateComponentQty(idx, qty) {
    setF({ ...f, components: f.components.map((c, i) =>
             i === idx ? { ...c, quantity_per_parent: qty } : c) });
  }
  function removeComponent(idx) {
    setF({ ...f, components: f.components.filter((_, i) => i !== idx) });
  }

  async function save() {
    if (!f.name.trim()) return toast.error("Name required.");
    const body = { name: f.name.trim() };
    if (f.category) body.category = f.category;
    if (f.default_unit_cost) body.default_unit_cost = Number(f.default_unit_cost);
    if (f.components.length === 1) {
      // Preserve the legacy Phase 7A.1 single-child shape when there's
      // exactly one component — so existing inference works unchanged.
      body.child_item_id = f.components[0].component_item_id;
      body.units_per_parent = Number(f.components[0].quantity_per_parent) || 1;
    }
    if (f.components.length > 0) {
      body.components = f.components.map((c) => ({
        component_item_id: c.component_item_id,
        quantity_per_parent: Number(c.quantity_per_parent) || 1,
      }));
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/purchase-items", body);
      toast.success("Item created.");
      onCreated(r.data.purchase_item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl border-2 border-[var(--ink)] w-full max-w-sm p-3 space-y-2"
            data-testid="create-purchase-item-modal">
        <div className="flex justify-between">
          <div className="font-bold">Create Purchase Item</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <input value={f.name} data-testid="cpi-name"
               onChange={(e) => setF({ ...f, name: e.target.value })}
               placeholder="Name * (e.g. Gold 50-Pack Box)"
               className="w-full border rounded-xl px-3 py-2 text-sm"/>
        <input value={f.category}
               onChange={(e) => setF({ ...f, category: e.target.value })}
               placeholder="Category (optional)"
               className="w-full border rounded-xl px-3 py-2 text-sm"/>
        <input type="number" step="0.01" value={f.default_unit_cost}
               data-testid="cpi-default-cost"
               onChange={(e) => setF({ ...f, default_unit_cost: e.target.value })}
               placeholder="Default unit cost (optional)"
               className="w-full border rounded-xl px-3 py-2 text-sm"/>
        <div className="border-t pt-1 text-[10px] uppercase text-[var(--smoke)]">
          Contains / Components (optional)
        </div>
        {f.components.map((c, i) => (
          <div key={i} className="flex gap-1 items-center border rounded-lg px-2 py-1"
                data-testid={`cpi-component-${i}`}>
            <div className="flex-1 text-xs truncate">{c.component_name}</div>
            <input type="number" min="1" value={c.quantity_per_parent}
                    data-testid={`cpi-component-qty-${i}`}
                    onChange={(e) => updateComponentQty(i, e.target.value)}
                    className="w-14 border rounded px-1 py-0.5 text-xs"/>
            <button onClick={() => removeComponent(i)}
                     className="text-red-600"><X size={14}/></button>
          </div>
        ))}
        <div className="relative">
          <input value={componentQ}
                  onChange={(e) => setComponentQ(e.target.value)}
                  placeholder="Search a component to add…"
                  data-testid="cpi-component-search"
                  className="w-full border rounded-xl px-3 py-1.5 text-xs"/>
          {componentResults.length > 0 && (
            <div className="absolute z-10 bg-white border rounded-xl mt-1 w-full shadow-lg text-xs max-h-40 overflow-auto">
              {componentResults.map((it) => (
                <button key={it.id} onClick={() => addComponent(it)}
                         data-testid={`cpi-add-component-${it.id}`}
                         className="w-full text-left px-3 py-1 hover:bg-gray-50">
                  {it.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={save} disabled={busy} data-testid="cpi-save-btn"
                className={`w-full rounded-2xl py-2 font-bold text-white ${
                  busy ? "bg-gray-400" : "bg-[var(--ink)]"
                }`}>
          {busy ? "Saving…" : "Create Item"}
        </button>
      </div>
    </div>
  );
}

function emptyLine() {
  return {
    _picker: true,     // start each new line in picker mode
    line_type: "sticker_lot", purchase_item_id: null,
    purchase_item_name: null,
    shopify_product_id: "", shopify_variant_id: "", product_title: "",
    variant: "", description: "",
    quantity: 1, unit_cost: "", line_cost: "",
  };
}

function PurchaseEditor({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [f, setF] = useState(() => existing ? {
    purchase_date: existing.purchase_date || todayISO(),
    vendor_name:   existing.vendor_name || "",
    purchase_vendor_id: existing.purchase_vendor_id || null,
    payments:      existing.payments && existing.payments.length > 0
                     ? existing.payments.map((p) => ({ ...p }))
                     : [{ method: existing.payment_method || "card",
                          amount: existing.total_cost || 0 }],
    tax:           existing.tax ?? 0,
    shipping:      existing.shipping ?? 0,
    fees:          existing.fees ?? 0,
    discount:      existing.discount ?? 0,
    notes:         existing.notes || "",
    lines:         (existing.lines || []).map((l) => ({
                     ...emptyLine(), _picker: false, ...l,
                     purchase_item_name: l.product_title || l.description || null,
                     unit_cost: l.unit_cost ?? "",
                     line_cost: l.line_cost ?? "",
                   })),
  } : {
    purchase_date: todayISO(),
    vendor_name: "",
    purchase_vendor_id: null,
    payments: [{ method: "card", amount: 0 }],
    tax: 0, shipping: 0, fees: 0, discount: 0, notes: "",
    lines: [emptyLine()],
  });
  const [busy, setBusy] = useState(false);
  const [vendorSug, setVendorSug] = useState([]);
  const [vendorFocus, setVendorFocus] = useState(false);
  const vendorTimer = useRef(null);

  function setLine(i, patch) {
    setF((prev) => ({ ...prev,
      lines: prev.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  }
  function addLine() { setF({ ...f, lines: [...f.lines, emptyLine()] }); }
  function removeLine(i) {
    if (f.lines.length <= 1) return;
    setF({ ...f, lines: f.lines.filter((_, idx) => idx !== i) });
  }

  function pickForLine(i, pick) {
    if (pick.kind === "purchase_item") {
      const it = pick.item;
      setLine(i, {
        _picker: false,
        line_type: it.shopify_variant_id ? "shopify_product" : "sticker_lot",
        purchase_item_id: it.id,
        purchase_item_name: it.name,
        shopify_product_id: it.shopify_product_id || "",
        shopify_variant_id: it.shopify_variant_id || "",
        product_title: it.shopify_variant_id ? it.name : "",
        description: it.shopify_variant_id ? "" : it.name,
        unit_cost: (it.last_unit_cost ?? it.default_unit_cost ?? "").toString(),
      });
    } else if (pick.kind === "shopify") {
      const s = pick.shopify;
      setLine(i, {
        _picker: false,
        line_type: "shopify_product",
        purchase_item_id: null,
        purchase_item_name: s.title,
        shopify_product_id: s.shopify_product_id || "",
        shopify_variant_id: s.shopify_variant_id || "",
        product_title: s.title,
        unit_cost: (s.unit_price || "").toString(),
      });
    }
  }

  function unclassifyLine(i) {
    setLine(i, { _picker: false, line_type: "unclassified",
                 purchase_item_id: null,
                 purchase_item_name: "One-Time / Unclassified",
                 description: "", unit_cost: "", line_cost: "" });
  }

  async function fetchVendorSuggestions(v) {
    if (!v || v.length < 1) { setVendorSug([]); return; }
    try {
      // Phase 7A.1 #2 — Reusable Vendors first, historical fallback.
      const [reuse, hist] = await Promise.all([
        api.get("/admin/purchase-vendors",
                  { params: { q: v, limit: 8, active: true } })
           .catch(() => ({ data: { vendors: [] } })),
        api.get("/admin/purchases/vendors/suggest",
                  { params: { q: v, limit: 6 } })
           .catch(() => ({ data: { vendors: [] } })),
      ]);
      const seen = new Set();
      const out = [];
      for (const r of (reuse.data?.vendors || [])) {
        if (seen.has(r.name)) continue;
        seen.add(r.name);
        out.push({ id: r.id, vendor_name: r.name, source: "reusable" });
      }
      for (const r of (hist.data?.vendors || [])) {
        if (seen.has(r.vendor_name)) continue;
        seen.add(r.vendor_name);
        out.push({ id: null, vendor_name: r.vendor_name,
                    source: "history", count: r.count });
      }
      setVendorSug(out);
    } catch { setVendorSug([]); }
  }

  async function createVendorInline() {
    const name = (f.vendor_name || "").trim();
    if (!name) return toast.error("Type a vendor name first.");
    try {
      const r = await api.post("/admin/purchase-vendors", { name });
      const v = r.data.vendor;
      setF((prev) => ({ ...prev, vendor_name: v.name,
                          purchase_vendor_id: v.id }));
      toast.success(r.data.status === "exists"
        ? "Selected existing vendor." : "Vendor created.");
      setVendorSug([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vendor create failed.");
    }
  }

  function setPayment(idx, patch) {
    setF({ ...f, payments: f.payments.map((p, i) => i === idx ? { ...p, ...patch } : p) });
  }
  function addPayment() {
    setF({ ...f, payments: [...f.payments, { method: "cash", amount: 0 }] });
  }
  function removePayment(idx) {
    if (f.payments.length <= 1) return;
    setF({ ...f, payments: f.payments.filter((_, i) => i !== idx) });
  }

  async function save() {
    if (!f.vendor_name.trim()) return toast.error("Vendor name required.");
    const body = {
      purchase_date: f.purchase_date,
      vendor_name: f.vendor_name.trim(),
      tax: Number(f.tax) || 0,
      shipping: Number(f.shipping) || 0,
      fees: Number(f.fees) || 0,
      discount: Number(f.discount) || 0,
      notes: f.notes || null,
      purchase_vendor_id: f.purchase_vendor_id || null,
      payments: f.payments.map((p) => ({ method: p.method,
                                          amount: Number(p.amount) || 0 })),
      lines: f.lines.filter((l) => !l._picker).map((l) => {
        const b = { line_type: l.line_type,
                    quantity: Number(l.quantity) || 1 };
        if (l.purchase_item_id) b.purchase_item_id = l.purchase_item_id;
        if (l.unit_cost !== "" && l.unit_cost != null)
          b.unit_cost = Number(l.unit_cost);
        if (l.line_cost !== "" && l.line_cost != null)
          b.line_cost = Number(l.line_cost);
        if (l.line_type === "shopify_product") {
          b.shopify_product_id = l.shopify_product_id || null;
          b.shopify_variant_id = l.shopify_variant_id || null;
          b.product_title = l.product_title || null;
        } else if (l.line_type === "sticker_lot") {
          b.variant = l.variant || null;
          b.description = l.description || null;
        } else if (l.line_type === "custom" || l.line_type === "unclassified") {
          b.description = l.description || null;
        }
        return b;
      }),
    };
    if (body.lines.length === 0) return toast.error("Add at least one item.");
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/admin/purchases/${existing.id}`, body);
      else await api.post("/admin/purchases", body);
      toast.success(isEdit ? "Purchase updated." : "Purchase added.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  }

  const subtotalPreview = f.lines.filter((l) => !l._picker).reduce((s, l) => {
    const uc = Number(l.unit_cost); const lc = Number(l.line_cost);
    const qty = Number(l.quantity) || 0;
    if (!Number.isNaN(uc) && l.unit_cost !== "") return s + qty * uc;
    if (!Number.isNaN(lc) && l.line_cost !== "") return s + lc;
    return s;
  }, 0);
  const totalPreview = subtotalPreview
    + (Number(f.tax) || 0) + (Number(f.shipping) || 0)
    + (Number(f.fees) || 0) - (Number(f.discount) || 0);
  const paymentsSum = f.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const paymentsMatch = Math.abs(paymentsSum - totalPreview) <= 0.01;

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-lg p-3 space-y-2 max-h-[92vh] overflow-auto"
           data-testid="purchase-editor-modal">
        <div className="flex items-center justify-between">
          <div className="font-bold">{isEdit ? "Edit Purchase" : "Add Purchase"}</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>

        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)]">Date</div>
          <input type="date" value={f.purchase_date}
                 data-testid="purchase-date"
                 onChange={(e) => setF({ ...f, purchase_date: e.target.value })}
                 className="w-full border rounded-xl px-2 py-1.5 text-sm"/>
        </div>

        <div className="relative">
          <input value={f.vendor_name}
                 data-testid="purchase-vendor"
                 onFocus={() => { setVendorFocus(true);
                                   fetchVendorSuggestions(f.vendor_name || ""); }}
                 onBlur={() => setTimeout(() => setVendorFocus(false), 200)}
                 onChange={(e) => { setF({ ...f, vendor_name: e.target.value,
                                             purchase_vendor_id: null });
                                     clearTimeout(vendorTimer.current);
                                     vendorTimer.current = setTimeout(
                                       () => fetchVendorSuggestions(e.target.value), 150); }}
                 placeholder="Vendor name * (search / create)"
                 className="w-full border rounded-xl px-3 py-2 text-sm"/>
          {f.purchase_vendor_id && (
            <div className="text-[10px] text-green-700 mt-0.5"
                  data-testid="vendor-linked">✓ linked to reusable vendor</div>
          )}
          {vendorFocus && (vendorSug.length > 0 ||
                              (f.vendor_name && f.vendor_name.length >= 1)) && (
            <div className="absolute z-10 bg-white border rounded-xl mt-1 w-full shadow-lg text-xs"
                  data-testid="vendor-suggestions">
              {vendorSug.map((v, idx) => (
                <button key={`${v.source}-${v.vendor_name}-${idx}`}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => { setF({ ...f, vendor_name: v.vendor_name,
                                                  purchase_vendor_id: v.id || null });
                                          setVendorSug([]); setVendorFocus(false); }}
                        data-testid={`vendor-sug-${v.vendor_name}`}
                        className="w-full text-left px-3 py-1 hover:bg-gray-50 flex justify-between">
                  <span>{v.vendor_name}</span>
                  <span className="text-[var(--smoke)]">
                    {v.source === "reusable" ? "· saved"
                                              : `· ${v.count || 0}× history`}
                  </span>
                </button>
              ))}
              {f.vendor_name && !vendorSug.some((v) =>
                  v.vendor_name.toLowerCase() === f.vendor_name.toLowerCase()) && (
                <button onMouseDown={(ev) => ev.preventDefault()}
                        onClick={createVendorInline}
                        data-testid="vendor-create-inline"
                        className="w-full text-left px-3 py-1 bg-yellow-50 hover:bg-yellow-100 border-t">
                  + Create vendor "<b>{f.vendor_name}</b>"
                </button>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2" data-testid="purchase-lines">
          {f.lines.map((l, i) => (
            <div key={i} className="border rounded-xl p-2 space-y-1"
                  data-testid={`purchase-line-${i}`}>
              {l._picker ? (
                <>
                  <div className="flex justify-between">
                    <div className="text-[10px] uppercase text-[var(--smoke)]">Line #{i + 1}</div>
                    {f.lines.length > 1 && (
                      <button onClick={() => removeLine(i)} className="text-red-600">
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                  <ItemPicker
                     onPick={(pick) => pickForLine(i, pick)}
                     onUnclassified={() => unclassifyLine(i)}/>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-start">
                    <div className="text-sm font-bold truncate">
                      {l.purchase_item_name || l.product_title || l.description || "(item)"}
                      {l.line_type === "shopify_product" && <span className="text-blue-600 text-[10px]"> · Shopify</span>}
                      {l.line_type === "unclassified" && <span className="text-orange-600 text-[10px]"> · unclassified</span>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setLine(i, { _picker: true,
                                                           purchase_item_id: null,
                                                           purchase_item_name: null })}
                              className="text-[10px] underline">change</button>
                      {f.lines.length > 1 && (
                        <button onClick={() => removeLine(i)} className="text-red-600">
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </div>
                  </div>
                  {l.line_type === "unclassified" && (
                    <input value={l.description}
                           onChange={(e) => setLine(i, { description: e.target.value })}
                           placeholder="Notes (e.g. 'FOR??')"
                           className="w-full border rounded-lg px-2 py-1 text-xs"/>
                  )}
                  <div className="grid grid-cols-3 gap-1 items-end">
                    <div>
                      <div className="text-[9px] uppercase text-[var(--smoke)]">Qty</div>
                      <input type="number" min="1" value={l.quantity}
                             data-testid={`purchase-line-qty-${i}`}
                             onChange={(e) => setLine(i, { quantity: e.target.value })}
                             placeholder="Qty"
                             className="w-full border rounded-lg px-2 py-1 text-xs"/>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-[var(--smoke)]">Unit $</div>
                      <input type="number" step="0.01" value={l.unit_cost}
                             data-testid={`purchase-line-unit-${i}`}
                             onChange={(e) => setLine(i, { unit_cost: e.target.value })}
                             placeholder="Unit $"
                             className="w-full border rounded-lg px-2 py-1 text-xs"/>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-[var(--smoke)]">Line $</div>
                      {(() => {
                        // For unclassified lines the line_cost IS the money,
                        // so keep it editable.  For sticker_lot bulk buys
                        // where the owner provided no unit_cost, allow
                        // manual entry too.  Everything else is a live
                        // computed display of qty × unit_cost so the
                        // owner sees the math immediately.
                        const qty = Number(l.quantity) || 0;
                        const uc  = Number(l.unit_cost);
                        const hasUnit = l.unit_cost !== "" && l.unit_cost != null && !Number.isNaN(uc);
                        const isBulkStickerLot = l.line_type === "sticker_lot" && !hasUnit;
                        const isUnclassified   = l.line_type === "unclassified";
                        if (isUnclassified || isBulkStickerLot) {
                          return (
                            <input type="number" step="0.01" value={l.line_cost}
                                   data-testid={`purchase-line-total-${i}`}
                                   onChange={(e) => setLine(i, { line_cost: e.target.value })}
                                   placeholder={isUnclassified ? "Total $ *" : "Line $ (bulk)"}
                                   className="w-full border rounded-lg px-2 py-1 text-xs"/>
                          );
                        }
                        const computed = hasUnit ? qty * uc : 0;
                        return (
                          <div data-testid={`purchase-line-total-${i}`}
                                className="w-full border rounded-lg px-2 py-1 text-xs bg-gray-50 font-mono text-right">
                            ${computed.toFixed(2)}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
          <button onClick={addLine} data-testid="purchase-add-line"
                  className="text-xs border-2 border-[var(--ink)] rounded-full px-2 py-0.5">
            + Add another item
          </button>
        </div>

        <div className="grid grid-cols-4 gap-1 text-xs">
          <NumberField label="Tax" v={f.tax} on={(x) => setF({ ...f, tax: x })} testid="purchase-tax"/>
          <NumberField label="Shipping" v={f.shipping} on={(x) => setF({ ...f, shipping: x })} testid="purchase-shipping"/>
          <NumberField label="Fees" v={f.fees} on={(x) => setF({ ...f, fees: x })} testid="purchase-fees"/>
          <NumberField label="Discount" v={f.discount} on={(x) => setF({ ...f, discount: x })} testid="purchase-discount"/>
        </div>

        <div className="border-t pt-2 space-y-1" data-testid="purchase-payments">
          <div className="flex justify-between text-[10px] uppercase text-[var(--smoke)]">
            <span>Payments (split allowed)</span>
            <button onClick={addPayment} className="underline"
                     data-testid="add-payment-btn">+ split</button>
          </div>
          {f.payments.map((p, i) => (
            <div key={i} className="flex gap-1" data-testid={`payment-row-${i}`}>
              <select value={p.method}
                      onChange={(e) => setPayment(i, { method: e.target.value })}
                      data-testid={`payment-method-${i}`}
                      className="border rounded-lg px-2 py-1 text-xs">
                {PAY_METHODS.map((m) => <option key={m} value={m}>{PAY_LABELS[m]}</option>)}
              </select>
              <input type="number" step="0.01" value={p.amount}
                     onChange={(e) => setPayment(i, { amount: e.target.value })}
                     data-testid={`payment-amount-${i}`}
                     placeholder="Amount"
                     className="flex-1 border rounded-lg px-2 py-1 text-xs"/>
              {f.payments.length > 1 && (
                <button onClick={() => removePayment(i)} className="text-red-600">
                  <X size={12}/>
                </button>
              )}
            </div>
          ))}
          <div className={`text-[11px] flex justify-between ${
              paymentsMatch ? "text-green-700" : "text-red-700"}`}>
            <span>Payments sum</span>
            <span data-testid="payments-sum">{money(paymentsSum)} / {money(totalPreview)}</span>
          </div>
          {!paymentsMatch && (
            <button
              onClick={() => setF({ ...f, payments: [{ method: f.payments[0].method || "card",
                                                        amount: totalPreview }] })}
              data-testid="payments-match-total"
              className="text-[10px] underline">
              Match one payment to total
            </button>
          )}
        </div>

        <textarea value={f.notes}
                  onChange={(e) => setF({ ...f, notes: e.target.value })}
                  placeholder="Notes (optional)"
                  className="w-full border rounded-xl px-3 py-2 text-sm h-12"/>

        <div className="flex justify-between text-sm border-t pt-1">
          <span>Preview total</span>
          <b data-testid="purchase-total-preview">{money(totalPreview)}</b>
        </div>

        <button onClick={save} disabled={busy || !paymentsMatch}
                data-testid="purchase-save-btn"
                className={`w-full rounded-2xl py-2 font-bold text-white ${
                  busy || !paymentsMatch ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                }`}>
          {busy ? "Saving…" : isEdit ? "Save Changes" : "Add Purchase"}
        </button>
      </div>
    </div>
  );
}

function NumberField({ label, v, on, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-[var(--smoke)]">{label}</div>
      <input type="number" step="0.01" value={v}
             data-testid={testid}
             onChange={(e) => on(e.target.value)}
             className="w-full border rounded-lg px-2 py-1"/>
    </div>
  );
}
